import { useEffect, useState } from "react";
import type { UserRole } from "../../types/vtc";
import { deleteReview, editReview, getMyReviews, writeReview } from "../../lib/reviewApi";
import type { MyReviews, ReviewRow, ToReview } from "../../lib/reviewApi";
import { errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import { cn, longDate } from "../../lib/format";
import { useT } from "../../i18n";

const day = (v: string | null) => (v ? longDate(v.slice(0, 10)) : "");
const period = (start: string | null, end: string | null) => (start && end ? `${day(start)} → ${day(end)}` : "");

function Stars({ value, className }: { value: number; className?: string }) {
  const full = Math.round(value);
  return (
    <span className={cn("tracking-[1px]", className ?? "text-[13px]")} aria-label={`${value}/5`}>
      <span className="text-ink">{"★".repeat(full)}</span>
      <span className="text-[#D4D4D4]">{"★".repeat(5 - full)}</span>
    </span>
  );
}

/**
 * "Reviews" page of the driver and company areas: rating summary, completed
 * bookings waiting for a review, reviews posted (edit / delete; an edit goes
 * back to moderation) and published reviews about the account.
 */
export default function ReviewsPanel({ role }: { role: UserRole }) {
  const t = useT();
  const r = t.reviews;
  const [data, setData] = useState<MyReviews | null>(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const refresh = () => setReload((n) => n + 1);

  useEffect(() => {
    getMyReviews(role)
      .then(setData)
      .catch((err) => setError(errorText(err)));
  }, [role, reload]);

  if (error) return <p className="m-0 rounded-card bg-white p-6 text-sm text-tone-red-fg">{error}</p>;
  if (!data) return <div className="h-40 animate-pulse rounded-card bg-white" aria-busy="true" />;

  const box = "m-0 rounded-card bg-white px-4 py-4 text-sm text-ink-soft";

  return (
    <div className="grid max-w-240 gap-6">
      {/* Rating summary */}
      <div className="flex items-center gap-4 rounded-card bg-white px-5 py-5">
        <span className="min-w-12 text-[32px] font-medium">{data.rating.average ?? "—"}</span>
        <span>
          <Stars value={data.rating.average ?? 0} className="text-base" />
          <span className="block text-xs text-muted">
            {role === "driver" ? r.ratingFromCompanies : r.ratingFromDrivers} · {r.count(data.rating.count)}
          </span>
        </span>
      </div>

      {data.toWrite.length > 0 && (
        <section>
          <h2 className="m-0 mb-2.5 text-[17px] font-medium">{r.toWriteTitle}</h2>
          <div className="grid gap-2">
            {data.toWrite.map((b) => (
              <WriteCard key={b.bookingId} role={role} booking={b} onDone={refresh} />
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="m-0 mb-2.5 text-[17px] font-medium">{r.writtenTitle}</h2>
        {data.written.length ? (
          <div className="grid gap-2">
            {data.written.map((row) => (
              <PostedCard key={row.id + row.updatedAt} role={role} row={row} onChanged={refresh} />
            ))}
          </div>
        ) : (
          <p className={box}>{r.noneWritten}</p>
        )}
      </section>

      <section>
        <h2 className="m-0 mb-2.5 text-[17px] font-medium">{r.receivedTitle}</h2>
        {data.received.length ? (
          <div className="grid gap-2">
            {data.received.map((row) => (
              <div key={row.id} className="rounded-card bg-white p-4">
                <b className="text-sm font-medium">{row.by}</b>
                <div className="text-xs text-muted">{[row.vehicle, period(row.rentalStart, row.rentalEnd)].filter(Boolean).join(" · ")}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <Stars value={row.rating} />
                  <span className="text-xs text-muted">{day(row.createdAt)}</span>
                </div>
                <p className="m-0 mt-1.5 whitespace-pre-line text-[13px] leading-relaxed">{row.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className={box}>{r.noneReceived}</p>
        )}
      </section>
    </div>
  );
}

/** Star picker + text, used to write and to edit a review. */
function ReviewForm({
  role,
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  role: UserRole;
  initial?: { rating: number; text: string };
  submitLabel: string;
  onSubmit: (rating: number, text: string) => Promise<void>;
  onCancel: () => void;
}) {
  const t = useT();
  const r = t.reviews;
  const [rating, setRating] = useState(initial?.rating ?? 0);
  const [text, setText] = useState(initial?.text ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!rating) return setError(r.ratingRequired);
    if (text.trim().length < 10) return setError(r.textRequired);
    setBusy(true);
    setError("");
    try {
      await onSubmit(rating, text.trim());
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 grid gap-2.5">
      <div className="flex gap-1" role="radiogroup" aria-label={r.ratingLabel}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={rating === n}
            aria-label={`${n}/5`}
            onClick={() => setRating(n)}
            className={cn("cursor-pointer border-0 bg-transparent p-0 text-2xl leading-none", n <= rating ? "text-ink" : "text-[#D4D4D4]")}
          >
            ★
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={role === "driver" ? r.placeholderDriver : r.placeholderCompany}
        className="w-full resize-y rounded border border-transparent bg-surface px-3.5 py-3 text-sm outline-none focus:border-brand"
      />
      {error && <p className="m-0 text-xs text-tone-red-fg">{error}</p>}
      <p className="m-0 text-xs text-faint">{r.pendingNote}</p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void submit()}
          className="h-9 cursor-pointer rounded border-0 bg-ink px-4 text-[13px] font-medium text-white disabled:opacity-50"
        >
          {busy ? r.sending : submitLabel}
        </button>
        <button type="button" disabled={busy} onClick={onCancel} className="h-9 cursor-pointer rounded border-0 bg-surface px-4 text-[13px] font-medium">
          {t.common.cancel}
        </button>
      </div>
    </div>
  );
}

function WriteCard({ role, booking: b, onDone }: { role: UserRole; booking: ToReview; onDone: () => void }) {
  const r = useT().reviews;
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-card bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <b className="text-sm font-medium">{b.about}</b>
          <div className="text-xs text-muted">{[b.vehicle, period(b.start, b.end)].filter(Boolean).join(" · ")}</div>
        </div>
        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="h-9 cursor-pointer rounded border-0 bg-ink px-4 text-[13px] font-medium text-white hover:opacity-90"
          >
            {r.write}
          </button>
        )}
      </div>
      {open && (
        <ReviewForm
          role={role}
          submitLabel={r.submit}
          onCancel={() => setOpen(false)}
          onSubmit={async (rating, text) => {
            toast.success(await writeReview(role, b.bookingId, rating, text));
            onDone();
          }}
        />
      )}
    </div>
  );
}

function PostedCard({ role, row, onChanged }: { role: UserRole; row: ReviewRow; onChanged: () => void }) {
  const r = useT().reviews;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!window.confirm(r.deleteConfirm)) return;
    setBusy(true);
    try {
      toast.success(await deleteReview(role, row.id));
      onChanged();
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  };

  return (
    <div className="rounded-card bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <b className="text-sm font-medium">{row.about}</b>
          <div className="text-xs text-muted">{[row.vehicle, period(row.rentalStart, row.rentalEnd)].filter(Boolean).join(" · ")}</div>
          {!editing && (
            <>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Stars value={row.rating} />
                <span className="text-xs text-muted">{day(row.createdAt)}</span>
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[11px] font-medium",
                    row.status === "published" && "bg-tone-green-bg text-tone-green-fg",
                    row.status === "pending" && "bg-tone-amber-bg text-tone-amber-fg",
                    row.status === "rejected" && "bg-tone-red-bg text-tone-red-fg",
                  )}
                >
                  {r.status[row.status]}
                </span>
              </div>
              {row.status !== "published" && (
                <p className="m-0 mt-1 text-[11px] text-muted">{row.status === "pending" ? r.pendingNote : r.rejectedNote}</p>
              )}
              <p className="m-0 mt-1.5 whitespace-pre-line text-[13px] leading-relaxed">{row.text}</p>
            </>
          )}
        </div>
        {!editing && (
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={busy}
              onClick={() => setEditing(true)}
              className="h-8 cursor-pointer rounded border-0 bg-surface px-3 text-xs font-medium hover:bg-surface-hover"
            >
              {r.edit}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void remove()}
              className="h-8 cursor-pointer rounded border-0 bg-transparent px-2 text-xs font-medium text-tone-red-fg hover:bg-tone-red-bg"
            >
              {r.delete}
            </button>
          </div>
        )}
      </div>
      {editing && (
        <ReviewForm
          role={role}
          initial={{ rating: row.rating, text: row.text }}
          submitLabel={r.saveEdit}
          onCancel={() => setEditing(false)}
          onSubmit={async (rating, text) => {
            toast.success(await editReview(role, row.id, rating, text));
            onChanged();
          }}
        />
      )}
    </div>
  );
}
